import { FC, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import * as Sentry from "@sentry/react";

import "@src/stvUtilsFrontend.scss";
import "@src/global.scss";

import reduxStore, { useAppDispatch, useAppSelector } from "@src/reduxStore";
import TopMenu from "@src/TopMenu";
import { Provider as ReduxProvider } from "react-redux";
import { IntlProviderBoundary } from "@src/i18n";
import ErrorPage from "@src/ErrorPage";
import rpApi, { useConfig, useUserInfo } from "@src/common/reliablePartnerApi";

import LazyAdminPage from "@src/admin/LazyAdminPage";
import Footer from "@src/Footer";
import MainContent from "@src/MainContent";
import SubMenu from "@src/SubMenu";
import { changeOrg, setConfig } from "@src/common/commonSlice";
import { setLogger } from "react-query";

const SentryInitializer: FC = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [sessionScoped, setSessionScoped] = useState(false);
  const [userScoped, setUserScoped] = useState(false);

  const sentryDsn = useConfig().data?.sentryDsn;
  const release = process.env.CI_PIPELINE_ID;
  const sessionId = useAppSelector(state => state.common.sessionId);
  const userId = useUserInfo().data?.qvarnId;

  // Send the session id to server to include in the sentry events.
  if (rpApi.defaults.headers) {
    const headers = rpApi.defaults.headers as {
      common: Record<string, string>;
    };
    headers.common["X-Session-Id"] = sessionId;
  }

  useEffect(() => {
    if (!initialized && sentryDsn && release) {
      console.log(`Init Sentry ${sentryDsn}, release ${release}`);
      Sentry.init({
        release: `rp-client@${release}`,
        dsn: sentryDsn,
      });
      // Set the logger for the react-query.
      setLogger({
        error: error => Sentry.captureException(error),
        log: log => Sentry.captureEvent(log),
        warn: warn => Sentry.captureMessage(warn),
      });
      setInitialized(true);
    }
  }, [initialized, sentryDsn, release]);

  useEffect(() => {
    if (initialized && !sessionScoped && sessionId) {
      Sentry.configureScope(scope => scope.setTag("session_id", sessionId));
      setSessionScoped(true);
    }
  }, [initialized, sessionScoped, sessionId]);

  useEffect(() => {
    if (initialized && !userScoped && userId) {
      Sentry.setUser({ id: userId });
      setUserScoped(true);
    }
  }, [initialized, userScoped, userId]);

  return <>{children}</>;
};

const queryClient = new QueryClient();

const UserContext: FC = ({ children }) => {
  const dispatch = useAppDispatch();
  const userInfo = useUserInfo();
  let defaultOrg = userInfo.data?.companies.find(company => company.default);
  if (!defaultOrg && userInfo.data?.companies.length) {
    defaultOrg = userInfo.data.companies[0];
  }
  useEffect(() => {
    if (defaultOrg) {
      dispatch(changeOrg({ orgId: defaultOrg.qvarnId, ifPrevious: "default" }));
    }
  });

  return <>{children}</>;
};

const ConfigProvider: FC = ({ children }) => {
  const config = useConfig();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (config.isSuccess) {
      dispatch(setConfig(config.data));
    }
  }, [config, dispatch]);

  return <>{children}</>;
};

const FinnChatManager = () => {
  const locale = useAppSelector(state => state.common.language);

  useEffect(() => {
    document
      .getElementById("finnchat-language-indicator")
      ?.setAttribute("data-lang", locale === "fi" ? "fi" : "en");
  }, [locale]);

  return null;
};

const App = () => {
  return (
    <ReduxProvider store={reduxStore}>
      <IntlProviderBoundary>
        <Sentry.ErrorBoundary fallback={<ErrorPage />}>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <SentryInitializer>
                <ConfigProvider>
                  <FinnChatManager />
                  <UserContext>
                    <ReactQueryDevtools />
                    <TopMenu />
                    <SubMenu />
                    <div className="container-fluid main-container">
                      <div className="row justify-content-center">
                        <div className="col-12 col-sm-10 col-md-10 col-lg-10 col-xl-8">
                          <main>
                            <Switch>
                              <Route path="/admin">
                                <LazyAdminPage />
                              </Route>
                              <Route>
                                <MainContent />
                              </Route>
                            </Switch>
                          </main>
                        </div>
                        <div className="col-12 col-xl-11">
                          <Footer />
                        </div>
                      </div>
                    </div>
                  </UserContext>
                </ConfigProvider>
              </SentryInitializer>
            </QueryClientProvider>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </IntlProviderBoundary>
    </ReduxProvider>
  );
};

/**
 * Check if a login just happened and redirect the user to the path
 * that they attempted to access before being redirected to login.
 * The path was set by axios interceptor in /common/reliablePartnerApi.ts
 */
if (window.localStorage.postLoginPath) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const pathname = window.localStorage.postLoginPath;
  delete window.localStorage.postLoginPath;
  if (typeof pathname === "string") {
    window.location.pathname = pathname;
  }
}

ReactDOM.render(<App />, document.querySelector("#app"));
